import React, { useState } from "react";
import { Text } from "@/components/ui/Text";
import { cn } from "@/utils/cn";
import { MediaItem } from "@/core/MediaItem/MediaItem.model";
import { DocumentIcon, ChatIcon } from "@/components/icons";
import { AiAnalysisModal } from "./AiAnalysisModal";

interface SelectionBadgeProps {
  index?: number;
  showNumber?: boolean;
}

export const SelectionBadge: React.FC<SelectionBadgeProps> = ({
  index,
  showNumber = false,
}) => (
  <div className="w-6 h-6 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
    {showNumber && index !== undefined ? index + 1 : "✓"}
  </div>
);

interface UsageBadgeProps {
  count: number;
}

export const UsageBadge: React.FC<UsageBadgeProps> = ({ count }) => (
  <div
    className="bg-blue-500/90 text-white px-2 py-0.5 rounded-full text-xs font-medium shadow-xs flex items-center gap-1"
    title={`Used in ${count} post${count > 1 ? "s" : ""}`}
  >
    <DocumentIcon className="w-3 h-3" />
    <span>{count}</span>
  </div>
);

interface DurationBadgeProps {
  durationSeconds: number;
}

export const DurationBadge: React.FC<DurationBadgeProps> = ({ durationSeconds }) => {
  const formatDuration = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = Math.floor(seconds % 60);
    return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
  };

  return (
    <div className="bg-black/70 text-white text-xs px-2 py-1 rounded-sm">
      {formatDuration(durationSeconds)}
    </div>
  );
};

interface ProcessingBadgeProps {
  status: "processing" | "failed";
}

export const ProcessingBadge: React.FC<ProcessingBadgeProps> = ({ status }) => {
  if (status === "failed") {
    return (
      <div className="bg-red-900/80 text-white px-2 py-1 rounded-sm text-xs">
        Failed
      </div>
    );
  }

  return (
    <div className="bg-blue-900/80 text-white px-2 py-1 rounded-sm text-xs flex items-center gap-1">
      <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      <span>Processing</span>
    </div>
  );
};

interface CommentBadgeProps {
  comment?: string | null;
  onClick?: (e: React.MouseEvent) => void;
  showOnHover?: boolean;
}

export const CommentBadge: React.FC<CommentBadgeProps> = ({
  comment,
  onClick,
  showOnHover = false,
}) => {
  const hasComment = Boolean(comment && comment.trim().length > 0);
  return (
    <button
      className={cn(
        "group/comment relative",
        showOnHover && !hasComment && "opacity-0 group-hover/card:opacity-100 transition-opacity"
      )}
      title={hasComment ? (comment as string) : "Add comment"}
      onClick={onClick}
      data-interactive="true"
    >
      <div
        className={cn(
          "w-6 h-6 text-white rounded-full flex items-center justify-center shadow-xs cursor-pointer transition-colors",
          hasComment
            ? "bg-gray-700/80"
            : "bg-gray-400/60 hover:bg-gray-500/80"
        )}
      >
        <ChatIcon className="w-3.5 h-3.5" />
      </div>
      {hasComment && (
        <div className="absolute bottom-full left-0 mb-2 hidden group-hover/comment:block z-50 pointer-events-none">
          <div className="bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-lg max-w-xs whitespace-pre-wrap break-words">
            {comment}
          </div>
        </div>
      )}
    </button>
  );
};

interface LabelBadgeProps {
  label?: string;
  onClick?: (e: React.MouseEvent) => void;
  showOnHover?: boolean;
}

export const LabelBadge: React.FC<LabelBadgeProps> = ({
  label,
  onClick,
  showOnHover = false,
}) => (
  <button
    className={cn(
      "group/label relative",
      showOnHover && "opacity-0 group-hover/card:opacity-100 transition-opacity"
    )}
    title={label || "Add label"}
    onClick={onClick}
    data-interactive="true"
  >
    <div
      className={cn(
        "w-6 h-6 text-white rounded-full flex items-center justify-center text-xs font-medium shadow-xs cursor-pointer transition-colors",
        label
          ? "bg-gray-700/80"
          : "bg-gray-400/60 hover:bg-gray-500/80"
      )}
    >
      {label ? "i" : "+"}
    </div>
    {label && (
      <div className="absolute bottom-full left-0 mb-2 hidden group-hover/label:block z-50 pointer-events-none">
        <div className="bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap max-w-xs">
          {label}
        </div>
      </div>
    )}
  </button>
);

interface GroupBadgeProps {
  group: string;
  color?: string;
}

export const GroupBadge: React.FC<GroupBadgeProps> = ({ group, color }) => {
  const pastelColorFromString = (str: string) => {
    if (!str) return "#eee";
    const goldenAngle = 137.508;
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = Math.abs((hash * goldenAngle) % 360);
    const sat = 70 + (Math.abs(hash) % 10);
    const light = 72 + (Math.abs(hash) % 8);
    return `hsl(${hue}, ${sat}%, ${light}%)`;
  };

  return (
    <div
      className="w-4 h-4 rounded-full border-2 border-white shadow-xs"
      style={{
        backgroundColor: color || pastelColorFromString(group),
      }}
      title={group}
    />
  );
};

export const PublicBadge: React.FC = () => (
  <div
    className="bg-green-600/90 text-white px-1.5 py-0.5 rounded-sm text-xs font-medium shadow-xs flex items-center gap-1"
    title="Public"
  >
    <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
      />
    </svg>
  </div>
);

interface CarouselIndicatorProps {
  current: number;
  total: number;
}

export const CarouselIndicator: React.FC<CarouselIndicatorProps> = ({
  current,
  total,
}) => (
  <div className="flex gap-1">
    {Array.from({ length: total }, (_, index) => (
      <div
        key={index}
        className={cn(
          "w-2 h-2 rounded-full",
          index === current ? "bg-white" : "bg-white/50"
        )}
      />
    ))}
  </div>
);

interface ReelBadgeProps {}

export const ReelBadge: React.FC<ReelBadgeProps> = () => (
  <div className="bg-black/70 p-1.5 rounded-sm">
    <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
      <path d="M2 6a2 2 0 012-2h6a2 2 0 012 2v8a2 2 0 01-2 2H4a2 2 0 01-2-2V6zM14.553 7.106A1 1 0 0014 8v4a1 1 0 00.553.894l2 1A1 1 0 0018 13V7a1 1 0 00-1.447-.894l-2 1z" />
    </svg>
  </div>
);

interface QualityBadgeProps {
  score: number;
  onClick?: (e: React.MouseEvent) => void;
  showDetails?: boolean;
}

export const QualityBadge: React.FC<QualityBadgeProps> = ({
  score,
  onClick,
  showDetails = false,
}) => {
  const getScoreColor = (score: number) => {
    if (score >= 0.7) return "bg-green-500/90";
    if (score >= 0.4) return "bg-yellow-500/90";
    return "bg-red-500/90";
  };

  const getScoreEmoji = (score: number) => {
    if (score >= 0.7) return "🌟";
    if (score >= 0.4) return "👍";
    return "💡";
  };

  return (
    <button
      className={cn(
        "group/quality relative",
        onClick && "cursor-pointer"
      )}
      onClick={onClick}
      disabled={!onClick}
      data-interactive="true"
      title={`Instagram Appeal: ${(score * 100).toFixed(0)}%`}
    >
      <div
        className={cn(
          "px-2 py-1 rounded-sm text-xs font-medium text-white shadow-xs transition-all",
          getScoreColor(score),
          onClick && "hover:shadow-md"
        )}
      >
        <span className="mr-1">{getScoreEmoji(score)}</span>
        {(score * 100).toFixed(0)}%
      </div>
      {showDetails && (
        <div className="absolute bottom-full left-0 mb-2 hidden group-hover/quality:block z-50 pointer-events-none">
          <div className="bg-gray-900/95 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap">
            Instagram Appeal Score
          </div>
        </div>
      )}
    </button>
  );
};

interface DownloadStatusBadgeProps {
  status: 'checking' | 'downloading' | 'ready' | 'failed' | null;
}

export const DownloadStatusBadge: React.FC<DownloadStatusBadgeProps> = ({ status }) => {
  if (!status) return null;

  const getStatusConfig = () => {
    switch (status) {
      case 'checking':
        return {
          color: 'bg-blue-500/90',
          icon: (
            <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          ),
          label: 'Checking...',
        };
      case 'downloading':
        return {
          color: 'bg-orange-500/90',
          icon: (
            <svg className="w-3 h-3 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
          ),
          label: 'Preparing...',
        };
      case 'ready':
        return {
          color: 'bg-green-500/90',
          icon: (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          ),
          label: 'Ready',
        };
      case 'failed':
        return {
          color: 'bg-red-500/90',
          icon: (
            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          ),
          label: 'Failed',
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  return (
    <div
      className={cn(
        "text-white px-2 py-1 rounded-sm text-xs font-medium shadow-xs flex items-center gap-1",
        config.color
      )}
      title={`Download ${config.label}`}
    >
      {config.icon}
      <span>{config.label}</span>
    </div>
  );
};

export const AnalyzingBadge: React.FC = () => {
  return (
    <div
      className="bg-purple-500/90 text-white px-2 py-1 rounded-sm text-xs font-medium shadow-xs flex items-center gap-1"
      title="AI analysis in progress"
    >
      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
      </svg>
      <span>Analyzing</span>
    </div>
  );
};

interface RatingBadgeProps {
  rating: number;
}

export const RatingBadge: React.FC<RatingBadgeProps> = ({ rating }) => (
  <div
    className="group/rating relative flex flex-col-reverse items-center drop-shadow-md"
    title={`Client rating: ${rating}/5`}
  >
    {Array.from({ length: rating }, (_, i) => (
      <svg
        key={i}
        className="w-5 h-5 text-amber-400 -my-0.5"
        viewBox="0 0 24 24"
        fill="currentColor"
        stroke="currentColor"
        strokeWidth={0.5}
        strokeLinejoin="round"
      >
        <path
          className="stroke-amber-600"
          d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z"
        />
      </svg>
    ))}
  </div>
);

interface AiAnalysisProps {
  mediaItem: MediaItem;
  anchorPosition?: "left" | "right";
}

export const AiAnalysis: React.FC<AiAnalysisProps> = ({
  mediaItem,
  anchorPosition = "right",
}) => {
  const [showModal, setShowModal] = useState(false);
  const badgeRef = React.useRef<HTMLDivElement>(null);

  if (!mediaItem.frontendData.aiAnalysis?.instagramAppealScore) {
    return null;
  }

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowModal(!showModal);
  };

  return (
    <div ref={badgeRef}>
      <QualityBadge
        score={mediaItem.frontendData.aiAnalysis.instagramAppealScore}
        showDetails
        onClick={handleClick}
      />

      <AiAnalysisModal
        mediaItem={mediaItem}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        anchorElement={badgeRef.current}
        anchorPosition={anchorPosition}
      />
    </div>
  );
};
